import {
  MerchantEmployeeOutcomeKind,
  MerchantEmployeeOutcomeType,
  type MerchantEmployeeOutcomeDTO,
  type MerchantEmployeeOutcomeGroupDTO,
  type MerchantEmployeeOutcomeItemDTO,
  type MerchantEmployeeRefusalReasonDTO,
} from "@app/dto";

// Разбор справочника исходов и анкет — без запросов, поэтому отдельно от api.

export { MerchantEmployeeOutcomeKind as OutcomeKind };

// Анкета «что спрашивал клиент» ведётся в 1С шаблоном. В DTO сервера её пока нет — поля ниже
// описывают предложенное расширение контракта (config.questionnaires, questionnaireId у исхода и причины).
export interface QuestionnaireOption {
  id: string;
  name: string;
}

export interface QuestionnaireField {
  id: string;
  name: string;
  type: "select" | "number" | "text";
  required: boolean;
  // Только у select: выбирается несколько вариантов, ответ уходит в optionIds.
  multiple?: boolean;
  options?: QuestionnaireOption[];
  // Единица рядом с числом, например «₽».
  unit?: string;
  placeholder?: string;
}

export interface Questionnaire {
  id: string;
  name: string;
  // Заголовок карточки, например «Что спрашивал клиент — заявка в товарную матрицу».
  title: string;
  // У шаблона в 1С есть схема подбора: по этой анкете работает «Подобрать».
  pick?: boolean;
  fields: QuestionnaireField[];
}

export type RefusalReason = MerchantEmployeeRefusalReasonDTO & { questionnaireId?: string };
export type OutcomeItem = Omit<MerchantEmployeeOutcomeItemDTO, "refusalReasons"> & {
  refusalReasons: RefusalReason[];
  // Анкета по умолчанию: для причин без своей анкеты и для исходов без причин.
  questionnaireId?: string;
};
export type OutcomeNode = MerchantEmployeeOutcomeGroupDTO | OutcomeItem;

export function isGroup(node: MerchantEmployeeOutcomeDTO | OutcomeNode): node is MerchantEmployeeOutcomeGroupDTO {
  return node.type === MerchantEmployeeOutcomeType.GROUP;
}

// Все исходы, которые можно выбрать: элементы первого уровня и элементы внутри групп.
// Пусто — состояние завершается без исхода (в том числе когда у него одни пустые группы).
export function outcomeItems(nodes: (MerchantEmployeeOutcomeDTO | OutcomeNode)[]): OutcomeItem[] {
  return nodes.flatMap((node) => (isGroup(node) ? node.children : [node])) as OutcomeItem[];
}

// Анкета для исхода с выбранной причиной: своя у причины важнее анкеты исхода.
export function questionnaireOf(
  questionnaires: Questionnaire[],
  outcome: OutcomeItem,
  reasonId: string | null,
): Questionnaire | null {
  const reason = outcome.refusalReasons.find((item) => item.id === reasonId);
  const id = reason?.questionnaireId ?? outcome.questionnaireId;
  return questionnaires.find((questionnaire) => questionnaire.id === id) ?? null;
}

// Ответы одной анкеты: поле → выбранный вариант (варианты у multiple) или введённое значение.
export type QuestionnaireAnswers = Record<string, { optionId?: string; optionIds?: string[]; value?: string }>;

export function isAnswerFilled(answer: QuestionnaireAnswers[string] | undefined) {
  return Boolean(answer?.optionId || answer?.optionIds?.length || answer?.value?.trim());
}

// Ответ словами — для строки «Уже отмечено» и резюме подбора: «Кольцо · Золото 585, Серебро 925 · 70 000 ₽».
export function answersSummary(questionnaire: Questionnaire, answers: QuestionnaireAnswers) {
  return questionnaire.fields
    .map((field) => {
      const answer = answers[field.id];
      if (!isAnswerFilled(answer)) return null;
      if (field.type !== "select") {
        const value = answer!.value!.trim();
        return field.type === "number" ? `${Number(value.replace(/\s/g, "")).toLocaleString("ru-RU")} ${field.unit ?? ""}`.trim() : value;
      }
      const ids = answer!.optionIds ?? [answer!.optionId];
      return ids.map((id) => field.options?.find((option) => option.id === id)?.name ?? "").filter(Boolean).join(", ");
    })
    .filter(Boolean)
    .join(" · ");
}

export function questionnaireComplete(questionnaire: Questionnaire, answers: QuestionnaireAnswers) {
  return questionnaire.fields.every((field) => !field.required || isAnswerFilled(answers[field.id]));
}

export interface AnswerBody {
  fieldId: string;
  optionId?: string;
  optionIds?: string[];
  value?: string | number;
}

export function answersBody(questionnaire: Questionnaire, answers: QuestionnaireAnswers): AnswerBody[] {
  return questionnaire.fields.flatMap((field): AnswerBody[] => {
    const answer = answers[field.id];
    if (!isAnswerFilled(answer)) return [];
    if (field.multiple) return answer!.optionIds?.length ? [{ fieldId: field.id, optionIds: answer!.optionIds }] : [];
    if (answer!.optionId) return [{ fieldId: field.id, optionId: answer!.optionId }];
    const value = answer!.value!.trim();
    return [{ fieldId: field.id, value: field.type === "number" ? Number(value.replace(/\s/g, "")) : value }];
  });
}

// Один результат консультации в теле finish (предложенный контракт: finish { results: [...] }).
export interface ResultBody {
  outcomeId: string;
  reasonId?: string;
  documentId?: string;
  comment?: string;
  questionnaires?: {
    questionnaireId: string;
    answers: AnswerBody[];
  }[];
}

// Результат, отмеченный, но ещё не отправленный: консультация копит их до «Завершить».
export interface DraftResult {
  key: string;
  body: ResultBody;
  // Для полосы «Уже отмечено».
  label: string;
  detail?: string;
}
